/**
 * Busca local de sinais (PLAN §15).
 *
 * Roda inteiramente em memória sobre o catálogo já carregado do IndexedDB — nenhuma
 * chamada HTTP, nenhuma dependência de rede. São ~500 registros: um índice invertido
 * seria complexidade sem ganho perceptível, e a varredura linear responde em menos de
 * um milissegundo.
 */
import { normalizeText } from '@/lib/text';
import type { Sign } from '@/types/catalog';

/** Como o termo casou — define a ordem do resultado (F9-T04). */
export type MatchKind = 'exact' | 'prefix' | 'word-start' | 'substring' | 'reference';

/** Peso de cada tipo de casamento. Menor é melhor. */
const RANK: Record<MatchKind, number> = {
  exact: 0,
  prefix: 1,
  'word-start': 2,
  substring: 3,
  reference: 4,
};

export interface SearchResult {
  sign: Sign;
  kind: MatchKind;
  /** Posição do casamento no nome, usada como desempate. */
  position: number;
}

export interface SearchEntry {
  sign: Sign;
  normalizedName: string;
  normalizedReference: string;
}

/**
 * Prepara o catálogo para busca.
 *
 * O `normalizedName` vem pronto do catalogador, mas é renormalizado aqui por
 * segurança: um catálogo antigo pode ter sido gerado por uma versão anterior das
 * regras de normalização.
 */
export function buildSearchIndex(signs: Sign[]): SearchEntry[] {
  return signs.map((sign) => ({
    sign,
    normalizedName: sign.normalizedName || normalizeText(sign.name),
    normalizedReference: sign.reference ? normalizeText(sign.reference) : '',
  }));
}

function classify(haystack: string, needle: string): { kind: MatchKind; position: number } | null {
  if (haystack === needle) return { kind: 'exact', position: 0 };

  const at = haystack.indexOf(needle);
  if (at < 0) return null;

  if (at === 0) return { kind: 'prefix', position: 0 };

  // Início de palavra: "paz" deve rankear melhor em "A PAZ DE DEUS" do que em
  // "CAPAZ", onde o trecho cai no meio de uma palavra.
  const previous = haystack[at - 1];
  if (previous === ' ' || previous === '-') return { kind: 'word-start', position: at };

  return { kind: 'substring', position: at };
}

export interface SearchOptions {
  limit?: number;
  /** Inclui sinais desativados pela sincronização. Padrão: não. */
  includeInactive?: boolean;
  /** Também procura nas referências bíblicas. Padrão: sim. */
  searchReferences?: boolean;
}

export function searchSigns(
  index: SearchEntry[],
  query: string,
  options: SearchOptions = {},
): SearchResult[] {
  const { limit = 50, includeInactive = false, searchReferences = true } = options;

  const needle = normalizeText(query);
  if (needle.length === 0) return [];

  const results: SearchResult[] = [];

  for (const entry of index) {
    if (!includeInactive && !entry.sign.active) continue;

    const nameMatch = classify(entry.normalizedName, needle);
    if (nameMatch) {
      results.push({ sign: entry.sign, ...nameMatch });
      continue;
    }

    // A referência bíblica distingue homônimos ("JOSÉ de Arimatéia"), então buscar
    // nela é útil — mas sempre abaixo de qualquer casamento no nome.
    if (searchReferences && entry.normalizedReference.includes(needle)) {
      results.push({ sign: entry.sign, kind: 'reference', position: 0 });
    }
  }

  results.sort(
    (a, b) =>
      RANK[a.kind] - RANK[b.kind] ||
      a.position - b.position ||
      a.sign.name.length - b.sign.name.length ||
      a.sign.name.localeCompare(b.sign.name, 'pt-BR'),
  );

  return results.slice(0, limit);
}
